<template>
  <div class="col-12">
    <div class="card mb-5" style=" margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('groups.editGroup') }}
            </h6>
          </div>
        </div>
      </div>
    
      <LoadingSpinner v-if="isLoading" :message="t('groups.loadingData')" />

      <div v-else class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-6 ms-3">
            <GroupForm 
              ref="groupForm"
              :initial-data="initialGroupData"
              :is-edit="true"
              @submit="handleSubmit" 
              @cancel="handleCancel" 
              @validation-error="handleValidationError"
              @update:form-data="handleFormUpdate"
            />
            
            <div class="mt-4 d-flex gap-2">
              <button 
                type="button" 
                class="btn btn-primary btn-sm flex-grow-1"
                @click="submitForm"
                :disabled="isSubmitting || !hasChanges"
              >
                <span v-if="isSubmitting">
                  <i class="fas fa-spinner fa-spin me-2"></i>
                  {{ t('groups.saving') }}
                </span>
                <span v-else>
                  <i class="fas fa-save ms-2"></i>
                  {{ t('groups.saveChanges') }}
                </span>
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary btn-sm flex-grow-1"
                @click="handleCancel"
                :disabled="isSubmitting"
              >
                <i class="fas fa-times ms-2"></i>
                {{ t('groups.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import GroupForm from '@/components/features/groups/forms/GroupForm.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const toast = useToast();
const router = useRouter();
const route = useRoute();
const store = useStore();

const groupForm = ref(null);
const isSubmitting = ref(false);
const isLoading = ref(true);
const initialGroupData = ref({ name: '' });
const originalGroupData = ref({ name: '' });
const currentFormData = ref({ name: '' });

const hasChanges = computed(() => {
  return currentFormData.value.name !== originalGroupData.value.name;
});

const handleFormUpdate = (newData) => {
  currentFormData.value = { ...newData };
};

onMounted(async () => {
  try {
    const groupId = route.params.id;
    if (!groupId) {
      toast.error(t('groups.invalidGroupId'));
      router.push('/groups');
      return;
    }

    const response = await store.dispatch('groups/fetchGroup', groupId);
    initialGroupData.value = { name: response.data.name };
    originalGroupData.value = { name: response.data.name };
    currentFormData.value = { name: response.data.name };
  } catch (error) {
    console.error('Error loading group:', error);
    toast.error(t('groups.failedLoadData'));
    router.push('/groups');
  } finally {
    isLoading.value = false;
  }
});

const handleSubmit = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t('groups.noChanges'));
    return;
  }

  isSubmitting.value = true;
  try {
    const groupId = route.params.id;
    await store.dispatch('groups/updateGeneralGroup', { 
      groupId, 
      groupData: { name: formData.name } 
    });
    toast.success(t('groups.updatedSuccessfully'));
    router.push('/groups');
  } catch (error) {
    console.error('Error updating group:', error);
    
    if (error.response?.status === 422) {
      const errors = error.response.data.errors || {};
      groupForm.value.setServerErrors(errors);
      toast.error(t('groups.correctFormErrors'));
    } else {
      toast.error(error.response?.data?.message || t('groups.updateFailed'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const submitForm = () => {
  groupForm.value.submit();
};

const handleValidationError = () => {
  toast.warning(t('groups.validationError'));
};

const handleCancel = () => {
  router.push('/groups');
};
</script>
